// gatsby-browser.js (stable Yotpo init)

const DEFAULT_DEBOUNCE_MS = 180;
let lastInitAt = 0;
let debounceTimer = null;
let observer = null;

const getContainer = () =>
  window.yotpoWidgetsContainer || window.yotpo || window.Yotpo;

function safeInitWidgets() {
  const cont = getContainer();
  if (!cont || typeof cont.initWidgets !== 'function') return false;
  try {
    cont.initWidgets();
  } catch (err) {
    // swallow — we'll retry via debounce/observer
  }
  lastInitAt = Date.now();
  return true;
}

function scheduleInit() {
  const now = Date.now();
  if (now - lastInitAt < 500) return;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => safeInitWidgets(), { timeout: 300 });
    } else {
      requestAnimationFrame(() => safeInitWidgets());
    }
  }, DEFAULT_DEBOUNCE_MS);
}

function waitForWidgetsAndInit(stableMs = 300) {
  const widgetSelector = '.yotpo-widget-instance, .yotpo-main-widget';
  const nodes = Array.from(document.querySelectorAll(widgetSelector));
  if (!nodes.length) {
    scheduleInit();
    return;
  }

  if (typeof MutationObserver === 'undefined') {
    scheduleInit();
    return;
  }

  if (observer) {
    observer.disconnect();
    observer = null;
  }

  let lastMutated = Date.now();
  observer = new MutationObserver(() => {
    lastMutated = Date.now();
  });

  nodes.forEach(n =>
    observer.observe(n, { childList: true, subtree: true, attributes: true })
  );

  const poll = () => {
    if (Date.now() - lastMutated >= stableMs) {
      observer.disconnect();
      observer = null;
      scheduleInit();
      return;
    }
    requestAnimationFrame(poll);
  };
  requestAnimationFrame(poll);
}

// Runs once, right after Gatsby hydrates the SSR HTML
export const onInitialClientRender = () => {
  if (typeof window === 'undefined') return;

  // Force immediate init for star ratings (prevents them vanishing on hydration)
  const starNodes = document.querySelectorAll(
    '.yotpo-widget-instance[data-yotpo-instance-id]'
  );
  if (starNodes.length) {
    safeInitWidgets();
  }

  // Then run the normal stability watcher for everything else (e.g. reviews list)
  waitForWidgetsAndInit();
};

// Runs after every route change (including first one)
export const onRouteUpdate = ({ location }) => {
  if (typeof window === 'undefined') return;

  // If the container exists already, wait for DOM stability then init.
  if (getContainer()) {
    waitForWidgetsAndInit();
    return;
  }

  // If container missing, try to reload the loader once and then wait for widgets.
  if (!window.__yotpo_loader_reloaded) {
    const script = Array.from(document.scripts).find(
      s => s.src && s.src.includes('yotpo')
    );
    if (script) {
      window.__yotpo_loader_reloaded = true;
      try {
        script.remove();
      } catch (e) {
        /* ignore */
      }
      const s = document.createElement('script');
      s.src = script.src;
      s.async = true;
      s.onload = () => {
        // Force re-init immediately and then also wait for DOM stability
        safeInitWidgets();
        waitForWidgetsAndInit();
      };
      document.head.appendChild(s);
      return;
    }
  }

  // fallback
  scheduleInit();
};
